// The recorder's rules, pure and without React or the browser, so the
// self-test pins them (recorder.selftest.ts): how a part's index follows
// from its place in a take, what to do with each answer from the server,
// when a recording that was cut short finishes on its own, what the Mac
// app is told, and what the meeting picker says beside a meeting that
// already holds a recording.

import type { Meeting } from "@/api/meetings";
import { formatTime } from "@/lib/format";
import type { RecordingMessage } from "@/native/desktopRecording";

/** MediaRecorder hands over audio this often; each piece is stored on the device, then sent. */
export const TIMESLICE_MS = 5_000;
/** The most audio one PUT carries (the edge in front of the api caps request bodies; 1 MB is nginx's default). */
export const PART_BYTES = 768 * 1024;
/** A part PUT that has not answered in this long is given up and tried again. */
export const PUT_TIMEOUT_MS = 60_000;
/** keepalive requests may carry at most 64 KB in total. */
export const KEEPALIVE_BYTES = 60 * 1024;
/** A take whose tab has said nothing for this long is treated as gone (when Web Locks are missing). */
export const OWNER_STALE_MS = 90_000;
/** A recording cut short on a calendar meeting finishes on its own this long after the meeting's end. */
export const AFTER_END_MS = 15 * 60_000;
/** …and one on a meeting not on the calendar, this long after its last audio. */
export const AFTER_LAST_AUDIO_MS = 30 * 60_000;
/** The level under which the microphone counts as hearing nothing. */
export const QUIET_LEVEL = 0.02;
/** Silence this long is worth a word ("check the microphone"). */
export const QUIET_WARN_SEC = 60;
/** A microphone that keeps dropping is carried on by itself at most this often, then the person is asked. */
export const AUTO_CARRY_MAX = 3;
export const AUTO_CARRY_WINDOW_MS = 2 * 60_000;
/**
 * How long a flush (the Mac app, before a quit) waits for the slice it asked for, or a
 * stopping take's last piece: under the app's own 3 s, so a confirmed write can end its wait.
 */
export const FLUSH_WAIT_MS = 2_500;
/** A system mute this long counts as a stretch not recorded (shorter ones are a blip). */
export const MUTE_GAP_MS = 10_000;

/**
 * Who recorded a take: the sign-in's role, user and (a child's) agent. Only the same
 * sign-in takes a take over, sends it, shows it or offers it (another person signing in
 * on this browser never sees or files it).
 */
export function ownerKey(p: { role?: string | null; user?: string | null; agent?: string | null }): string {
  return [p.role ?? "", p.user ?? "", p.agent ?? ""].join("|");
}

// ─── part indexes ───
//
// A take's audio is a run of local parts, seq 0, 1, 2… (each ≤ PART_BYTES).
// Normally part seq N is upload index N. When a take has to move to a new
// upload part-way (the server no longer has the old one), the parts not yet
// sent go to the new upload from index 1, and index 0 is a copy of part 0:
// it holds the container's header (WebM / MP4 init), without which the rest
// cannot be decoded. Part 0 is therefore kept on the device until the take
// is done.

export interface UploadRef {
  uploadId: string;
  /** The first local part this upload carries after the header (0 for a take's first upload). */
  base: number;
}

/** The server index of local part `seq` in `upload`. */
export function indexFor(upload: UploadRef, seq: number): number {
  if (upload.base === 0) return seq;
  return seq === 0 ? 0 : seq - upload.base + 1;
}

/** The local part a server index of `upload` stands for (index 0 of a moved upload is part 0). */
export function seqFor(upload: UploadRef, index: number): number {
  if (upload.base === 0) return index;
  return index === 0 ? 0 : upload.base + index - 1;
}

/** Splits a blob's byte length into part ranges of at most `size` bytes. */
export function partRanges(bytes: number, size = PART_BYTES): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let off = 0; off < bytes; off += size) out.push([off, Math.min(off + size, bytes)]);
  return out;
}

// ─── server answers ───

export type Verdict =
  | "ok"
  | "retry"          // no answer, a gateway, the api restarting: try again with backoff
  | "signed_out"     // 401: wait for a sign-in; nothing is lost
  | "storage_full"   // 507: the server's disk is full; try again slowly
  | "move"           // wrong_upload / no_upload: register again with the same client_ref
  | "kept"           // part_exists: the server holds other audio at that index; keep this part, go on
  | "empty"          // empty_recording: the take holds no audio; it is final
  | "refused";       // anything else: say so, keep the audio, try again slowly

/** What a failed part PUT or finish means for the audio. */
export function verdictFor(status: number, code: string | null): Verdict {
  if (status === 0 || status === 408 || status === 429 || status === 502 || status === 503 || status === 504) return "retry";
  if (status === 401) return "signed_out";
  if (status === 507 || code === "storage_full") return "storage_full";
  if (code === "wrong_upload" || code === "no_upload") return "move";
  if (code === "part_exists") return "kept";
  if (code === "empty_recording") return "empty";
  if (status >= 500) return "retry";
  return "refused";
}

/** The wait before the next try: 1, 2, 4… seconds, capped. */
export function backoffMs(attempt: number, capMs = 30_000): number {
  return Math.min(capMs, 1000 * 2 ** Math.max(0, Math.min(attempt, 10)));
}

// ─── recordings cut short ───

/** When a recording cut short finishes on its own if nobody answers (ms). */
export function autoFinishAt(meetingEndAt: string | null | undefined, lastAudioAt: number): number {
  const end = meetingEndAt ? Date.parse(meetingEndAt) : NaN;
  if (Number.isFinite(end)) return Math.max(end + AFTER_END_MS, lastAudioAt + 60_000);
  return lastAudioAt + AFTER_LAST_AUDIO_MS;
}

/** Seconds of audio in a take, from the times its pieces arrived: a sleeping laptop's gap is not counted. */
export function addAudio(totalSec: number, prevAt: number, now: number, sliceMs = TIMESLICE_MS): number {
  const delta = Math.max(0, now - prevAt);
  return totalSec + Math.min(delta, sliceMs * 1.6) / 1000;
}

const REASON_WORDS: Record<string, string> = {
  page_closed: "the page closed",
  microphone_lost: "the microphone stopped",
  call_lost: "the call's sound and the microphone stopped",
  signed_out: "you were signed out",
};

/** Whether a microphone that dropped may carry on by itself again (`times`: when it last did). */
export function mayAutoCarry(times: number[], now: number): boolean {
  return times.filter((t) => now - t < AUTO_CARRY_WINDOW_MS).length < AUTO_CARRY_MAX;
}

/** "Your recording of Weekly catch-up stopped at 13:14 when the page closed. 14 min are saved." */
export function cutShortLine(title: string, at: number, reason: string, savedSec: number): string {
  const why = REASON_WORDS[reason] ?? "something stopped it";
  // The number stays with its unit ("11 min" never splits over two lines).
  const saved = savedSec >= 60 ? `${Math.round(savedSec / 60)}\u00a0min are saved` : "what was recorded is saved";
  return `Your recording of ${title} stopped at ${formatTime(new Date(at))} when ${why}. ${saved[0].toUpperCase()}${saved.slice(1)}.`;
}

// ─── the meeting picker ───

/** Whether starting on this meeting adds to a recording it already has (continue_recording). */
export function hasRecording(m: Pick<Meeting, "recording_status">): boolean {
  return m.recording_status != null;
}

/** Minutes a meeting's recording holds so far, or null when the server has not said. */
export function recordedMinutes(m: Pick<Meeting, "recording_duration_sec" | "recording_segments">): number | null {
  const segs = m.recording_segments ?? [];
  const sum = segs.reduce((n, s) => n + (s.duration_sec ?? 0), 0);
  const sec = sum > 0 ? sum : (m.recording_duration_sec ?? 0);
  return sec > 0 ? Math.max(1, Math.round(sec / 60)) : null;
}

/** The note beside a meeting in the picker: "8 min recorded — Continue", or null. */
export function continueNote(
  m: Pick<Meeting, "recording_status" | "recording_duration_sec" | "recording_segments" | "recording_last_part_at">,
  now = Date.now(),
): string | null {
  if (!hasRecording(m)) return null;
  if (m.recording_status === "uploading") {
    const last = m.recording_last_part_at ? Date.parse(m.recording_last_part_at) : NaN;
    if (Number.isFinite(last) && now - last < 2 * 60_000) return "Being recorded now";
  }
  const min = recordedMinutes(m);
  return min ? `${min} min recorded — Continue` : "Part recorded — Continue";
}

/** The line under the picker when the chosen meeting already holds some of its recording, else null. */
export function addsToLine(
  m: Pick<Meeting, "recording_status" | "recording_duration_sec" | "recording_segments" | "recording_last_part_at">,
  now = Date.now(),
): string | null {
  const note = continueNote(m, now);
  if (!note) return null;
  if (note === "Being recorded now") return "Another device is recording this meeting now. This recording is added to it as its own part.";
  const min = recordedMinutes(m);
  return `${min ? `${min} min of it are` : "Part of it is"} recorded already. This recording is added to it, in order.`;
}

/** The picker's options with each recorded meeting's note on its label ("10:00 · Catch-up · 8 min recorded — Continue"). */
export function withContinueNotes<O extends { value: string; label: string }>(
  options: O[],
  cands: Array<Pick<Meeting, "id" | "recording_status" | "recording_duration_sec" | "recording_segments" | "recording_last_part_at">>,
  now = Date.now(),
): O[] {
  const byId = new Map(cands.map((m) => [m.id, m]));
  return options.map((o) => {
    const m = byId.get(o.value);
    const note = m ? continueNote(m, now) : null;
    return note ? { ...o, label: `${o.label} · ${note}` } : o;
  });
}

// ─── the Record screen ───

/** What the big button does: never blocked by an earlier recording that is still uploading. */
export function recordButton(s: {
  phase: string;
  capturing: boolean;
  offer: boolean;
  noAgent: boolean;
  supported: boolean;
}): { action: "stop" | "start" | "carryOn" | "none"; disabled: boolean; spinner: boolean } {
  if (s.phase === "recording") return { action: "stop", disabled: false, spinner: false };
  if (s.capturing) return { action: "none", disabled: true, spinner: true };
  const blocked = s.noAgent || !s.supported;
  return { action: s.offer ? "carryOn" : "start", disabled: blocked, spinner: false };
}

/** A deliberate Sign out asks first while a meeting is captured here or held for "Carry on". */
export function signOutAsks(s: { capturing: boolean; held: number }): boolean {
  return s.capturing || s.held > 0;
}

/** "Stop and upload it first" has finished: nothing captured, sent, finishing or held for this account. */
export function uploadSettled(s: { capturing: boolean; held: number; shellPhase: ShellPhase }): boolean {
  return !s.capturing && s.held === 0 && s.shellPhase === "idle";
}

// ─── the Mac app ───

export type ShellPhase = RecordingMessage["phase"];

/**
 * The phase the Mac app is told (decisions D1): starting = the microphone
 * is being asked for; recording = it is live on this page; uploading = no
 * microphone, parts still to send; finishing = no microphone, a finish in
 * flight or retried; idle = none of these (a take held for "Carry on" with
 * every part sent is idle with held > 0). The web never pauses a take on
 * its own page, so it never says "paused".
 */
export function shellPhaseOf(s: { starting: boolean; live: boolean; unsent: number; finishing: boolean }): ShellPhase {
  if (s.starting) return "starting";
  if (s.live) return "recording";
  if (s.unsent > 0) return "uploading";
  if (s.finishing) return "finishing";
  return "idle";
}

/** What the Mac app is told (mac-shell protocol v1, `recording`). */
export function shellRecording(s: {
  phase: ShellPhase;
  startedAt: number | null;
  title: string | null;
  unsent: number;
  stored: number | null;
  held: number;
  storage: "device" | "memory";
  clientRef: string | null;
  meetingId: string | null;
  bound: boolean;
  flushed: number;
}): RecordingMessage {
  return {
    type: "recording",
    active: s.phase === "starting" || s.phase === "recording" || s.phase === "paused",
    phase: s.phase,
    startedAt: s.startedAt ? new Date(s.startedAt).toISOString() : null,
    title: s.title,
    unsent: Math.max(0, s.unsent),
    stored: s.stored,
    held: Math.max(0, s.held),
    storage: s.storage,
    clientRef: s.clientRef,
    meetingId: s.meetingId,
    bound: s.bound,
    flushed: Math.max(0, s.flushed),
  };
}

/** A take that ended with no audio at all (D4: dropped here, never sent): what to check before trying again. */
export const NOTHING_RECORDED =
  "Nothing was recorded: the microphone gave no sound. Check it is connected and allowed, then try\u00a0again.";
